import { useMemo } from "react";
import {
  Card,
  Col,
  Empty,
  Progress,
  Row,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
} from "antd";
import { ColumnsType } from "antd/lib/table";
import { BotStatsForBot, useGetBotStatsQuery } from "api";
import { dateTimeFormat, format } from "libs";

interface BotStatsTabProps {
  botId: number;
}

export const BotStatsTab = ({ botId }: BotStatsTabProps) => {
  const { data, isLoading } = useGetBotStatsQuery({ botId });
  const stats = data?.data;

  if (isLoading || !stats) {
    return <Empty description="Se incarca statisticile..." />;
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      {/* Totals */}
      <Row gutter={16}>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Actiuni 24h" value={stats.totals.actions_24h} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Actiuni 7z" value={stats.totals.actions_7d} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic title="Actiuni 30z" value={stats.totals.actions_30d} />
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card>
            <Statistic
              title="Rata succes 30z"
              value={(stats.success_rate_30d * 100).toFixed(1)}
              suffix="%"
            />
          </Card>
        </Col>
      </Row>

      {/* Contribution */}
      <Card title="Aportul botului (cumulativ, lifetime)">
        <Row gutter={16}>
          <Col xs={8}>
            <Statistic
              title="Postari create"
              value={stats.contribution.posts_created}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="Aprecieri date"
              value={stats.contribution.likes_given}
            />
          </Col>
          <Col xs={8}>
            <Statistic
              title="Comentarii date"
              value={stats.contribution.comments_given}
            />
          </Col>
        </Row>
      </Card>

      {/* By type */}
      <Card title="Defalcare pe tip de actiune (30z)">
        <ByTypeTable data={stats.by_type_30d} />
      </Card>

      {/* Daily timeline */}
      <Card title="Activitate zilnica (30z)">
        <DailySparkline data={stats.actions_per_day_30d} />
      </Card>

      {/* Latency */}
      <Card title="Latenta medie pe tip (ms)">
        <Space wrap size="middle">
          {Object.entries(stats.avg_latency_ms_by_type).length === 0 && (
            <Typography.Text type="secondary">Niciun esantion.</Typography.Text>
          )}
          {Object.entries(stats.avg_latency_ms_by_type).map(([type, ms]) => (
            <Statistic
              key={type}
              title={type}
              value={ms}
              suffix="ms"
              valueStyle={{ fontSize: 18 }}
            />
          ))}
        </Space>
      </Card>

      {stats.last_action_at && (
        <Typography.Text type="secondary">
          Ultima actiune: {format(stats.last_action_at, dateTimeFormat)}
        </Typography.Text>
      )}
    </Space>
  );
};

interface ByTypeRow {
  action_type: string;
  success: number;
  failed: number;
  skipped: number;
  rate_limited: number;
  total: number;
  success_rate: number;
}

const ByTypeTable = ({
  data,
}: {
  data: BotStatsForBot["by_type_30d"];
}) => {
  const rows: ByTypeRow[] = useMemo(
    () =>
      Object.entries(data).map(([action_type, counts]) => {
        const total =
          counts.success + counts.failed + counts.skipped + counts.rate_limited;
        return {
          action_type,
          ...counts,
          total,
          success_rate: total > 0 ? counts.success / total : 0,
        };
      }),
    [data]
  );

  const columns: ColumnsType<ByTypeRow> = [
    {
      title: "Tip",
      dataIndex: "action_type",
      render: (t: string) => <Tag>{t}</Tag>,
    },
    { title: "Total", dataIndex: "total", align: "right" as const },
    { title: "Succes", dataIndex: "success", align: "right" as const },
    { title: "Esuat", dataIndex: "failed", align: "right" as const },
    { title: "Sarit", dataIndex: "skipped", align: "right" as const },
    {
      title: "Rate-limited",
      dataIndex: "rate_limited",
      align: "right" as const,
    },
    {
      title: "Rata succes",
      render: (_, r) => (
        <Progress
          percent={Math.round(r.success_rate * 100)}
          size="small"
          status={r.success_rate < 0.5 ? "exception" : "active"}
        />
      ),
    },
  ];

  return (
    <Table<ByTypeRow>
      columns={columns}
      dataSource={rows}
      pagination={false}
      rowKey="action_type"
      size="small"
      locale={{ emptyText: <Empty description="Nicio actiune in ultimele 30z" /> }}
    />
  );
};

const DailySparkline = ({
  data,
}: {
  data: BotStatsForBot["actions_per_day_30d"];
}) => {
  const max = useMemo(
    () => Math.max(1, ...data.map((d) => d.count)),
    [data]
  );

  return (
    <div style={{ display: "flex", gap: 2, alignItems: "flex-end", height: 80 }}>
      {data.map((d) => (
        <div
          key={d.day}
          title={`${d.day}: ${d.count} actiuni`}
          style={{
            flex: 1,
            background: d.count > 0 ? "#791169" : "#f0f0f0",
            height: `${(d.count / max) * 100}%`,
            minHeight: 2,
            borderRadius: 2,
            cursor: "default",
          }}
        />
      ))}
    </div>
  );
};
